import { ArrayField, BooleanField, ChipField, Datagrid, DateField, FunctionField, List, NumberField, ReferenceArrayField, ReferenceField, SearchInput, Show, SimpleShowLayout, SingleFieldList, Tab, TabbedShowLayout, TextField, useGetOne, useListContext, usePermissions, useRecordContext } from 'react-admin';
import Permission from '../../components/Permission';
import { PERMISSIONS, daysOfTheWeek, frequencyChoices } from '../../constants';

import { Box, Typography } from '@mui/material';
import { uniq } from 'lodash';
import moment from 'moment';
import { useState } from 'react';
import { getChainId, getUserId } from '../../lib';
import CloneScoringButton from './CloneScoringButton';
import ScoringDisableButton from './ScoringDisableButton';
import { PartiallyHiddenCpfField } from '../../components/Fields';

const containerFuelScoringSx = {
  padding: 0,
};

const SingleFieldChips = (props: any) => {
  const { data } = useListContext();
  return (
    !data || !data.length ?
      <FunctionField render={() => props.emptyText || 'Sem informações'} />
      :
      <SingleFieldList>
        <ChipField source={props.source} />
      </SingleFieldList>
  );
};

const FuelScoringsList = () => {
  const record = useRecordContext();
  if (!record) { return null; }

  return (
    <SimpleShowLayout sx={containerFuelScoringSx}>
      <ArrayField source="fuelScorings" label="Pontuações por combustíveis">
        <Datagrid style={{ padding: 0 }} bulkActionButtons={false}>
          <ReferenceField source="fuelId" reference="fuels" label="Combustível" link={false} sortable={false}>
            <TextField source="name" />
          </ReferenceField>
          <NumberField source="factor" label="Fator (pts/litro)" />
        </Datagrid>
      </ArrayField>
    </SimpleShowLayout>
  );
};

const StationListField = () => {
  const record = useRecordContext();
  if (!record) { return null; }

  return (
    <SimpleShowLayout sx={containerFuelScoringSx} record={{ ...record, stationIds: uniq(record.placeScorings?.map((ps: any) => ps.placeId)) }}>
      <ReferenceArrayField label="Postos" reference="stations" source="stationIds">
        <SingleFieldChips source="name" emptyText="Sem postos" />
      </ReferenceArrayField>
    </SimpleShowLayout>
  );
};

const CustomerTagListField = () => {
  const record = useRecordContext();
  if (!record) { return null; }

  return (
    <SimpleShowLayout sx={containerFuelScoringSx} record={{ ...record }}>
      <ReferenceArrayField label="Tags" reference="chains/chainId/tag" source="customerTagIds">
        <SingleFieldChips source="name" emptyText="Sem Tags" />
      </ReferenceArrayField>
    </SimpleShowLayout>
  );
};

const CitiesFieldList = () => {
  const record = useRecordContext();
  if (!record) { return null; }

  return (
    <SimpleShowLayout sx={containerFuelScoringSx}>
      <ReferenceArrayField label="Cidades" reference="cities" source="cityIds">
        <SingleFieldList linkType={false} empty={<FunctionField render={() => 'Sem cidades'} />}>
          <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>
    </SimpleShowLayout>
  );
};

const DayScheduledScoringsField = ({ data }: any) => (
  data?.dayScheduledScorings ?
    <ArrayField source="dayScheduledScorings">
      <Datagrid bulkActionButtons={false}>
        <FunctionField render={(record: any) => record && daysOfTheWeek.find(wd => wd.id === record.dayOfTheWeek)?.name} label="Dia" />
        <FunctionField render={(record: any) => record && record.startTime ? moment(record.startTime).format('HH:mm') : '--'} label="Início" />
        <FunctionField render={(record: any) => record && record.endTime ? moment(record.endTime).format('HH:mm') : '--'} label="Fim" />
      </Datagrid>
    </ArrayField>
    :
    <></>
);

const ScheduleField = () => {
  const record = useRecordContext();
  if (!record) { return null; }

  return (
    (record.startAt || record.endAt || record.frequency) ?
      <Box component="div" sx={{ p: 2, border: '1px dashed grey', marginTop: 2, marginBottom: 2 }}>
        <Typography variant="body1" style={{ marginBottom: 10 }}>Agendamento</Typography>
        <SimpleShowLayout>
          <FunctionField render={(record: any) => record && record.startAt ? moment(record.startAt).format('DD/MM/YYYY HH:mm') : '--'} label="Data inicial" />
          <FunctionField render={(record: any) => record && record.endAt ? moment(record.endAt).format('DD/MM/YYYY HH:mm') : '--'} label="Data final" />
          <FunctionField render={(record: any) => record && record.frequency ? frequencyChoices.find(dt => dt.id === record.frequency)?.name : '--'} label="Frequência" />
          <DayScheduledScoringsField data={record} label="Horários" />
        </SimpleShowLayout>
      </Box>
      :
      <></>
  );
};

const CustomerList = () => {
  const record = useRecordContext();
  if (!record) { return null; }

  const listFilters = [
    <SearchInput source="search" alwaysOn />
  ];

  const CustomerDatagrid = () => (
    <Datagrid bulkActionButtons={false}>
      <TextField source="name" label="Nome" />
      <PartiallyHiddenCpfField source="cpf" label="CPF" />
    </Datagrid>
  );

  return (
    record.customerTagIds?.length ?
      <List
        resource="customers"
        title=" - Clientes"
        filter={{
          orFilters: {
            tagIds: record.customerTagIds,
          }
        }}
        filters={listFilters}
        exporter={false}
        disableSyncWithLocation
      >
        <CustomerDatagrid />
      </List>
      :
      <List
        resource="customer-scorings"
        title=" - Clientes"
        filter={{ scoringId: record.id }}
        filters={listFilters}
        exporter={false}
        disableSyncWithLocation
      >
        <CustomerDatagrid />
      </List>
  );
};

const ScoringShow = (props: any) => {
  const [chainId] = useState(getChainId());
  if (!chainId) { return <Typography>Selecione uma Rede</Typography>; }

  const { data: chain } = useGetOne('chains', { id: chainId });
  const { permissions } = usePermissions();

  return (
    <Permission permission={PERMISSIONS.SCORINGS}>
      <Show title='Pontuação' {...props}>
        <TabbedShowLayout>
          <Tab label="Resumo">
            <ScoringDisableButton style={{ marginTop: 20, marginBottom: 20 }} isOutlined />
            <CloneScoringButton variant="outlined" style={{ marginBottom: 20 }} />
            <TextField source="identify" label="Código" />
            <ReferenceField source="createdByEmployeeId" reference="employees" label="Criado por" link={permissions && permissions.includes(PERMISSIONS.UPDATE_USER)}>
              <FunctionField render={(record: any) => record ? `${record.name} ${record.id === getUserId() ? '(você)' : ''}` : ''} />
            </ReferenceField>
            <TextField source="description" label="Descrição" emptyText="--" />
            <CitiesFieldList />
            <CustomerTagListField />
            <StationListField />
            <TextField source="usageLimit" label="Limite de uso" emptyText="--" />
            <TextField source="intervalInHours" label="Intervalo de uso (horas)" emptyText="--" />
            <DateField source="disabledAt" label="Desativado em" showTime textAlign="center" emptyText="--" locales="pt-BR" />
            <ScheduleField />
          </Tab>
          <Tab label="Pontuações">
            <BooleanField source="overlapsStandardScoring" label="Sobrepor a pontuação padrão" />
            <FuelScoringsList />
          </Tab>
          <Tab label="Clientes">
            <CustomerList />
          </Tab>
        </TabbedShowLayout>
      </Show>
    </Permission>
  );
};

export default ScoringShow;
